import { apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import {
  associationPatchFromDraft,
  jobTypeIdsForSave,
  type EntityUsageDraft,
} from '@/lib/custom-fields/fieldModel';
import type {
  CreateAssociationDto,
  CreateDefinitionDto,
  CreateInheritanceRuleDto,
  CustomFieldAssociation,
  CustomFieldDefinition,
  CustomFieldInheritanceRule,
  UpdateDefinitionDto,
} from '@/types/custom-fields';

export interface CreateCustomFieldDefinitionPayload {
  definition: CreateDefinitionDto;
  usage: EntityUsageDraft[];
  isActive: boolean;
}

export async function createCustomFieldDefinition(
  payload: CreateCustomFieldDefinitionPayload,
): Promise<CustomFieldDefinition> {
  const created = await apiPost<CustomFieldDefinition, CreateDefinitionDto>(
    operationsClient,
    customFieldEndpoints.definitions(),
    payload.definition,
  );

  if (!payload.isActive) {
    await apiPatch<CustomFieldDefinition, UpdateDefinitionDto>(
      operationsClient,
      customFieldEndpoints.definition(created.id),
      { isActive: false },
    );
  }

  const usage = payload.usage.map((draft) => normalizeDraftForSave(draft, payload.usage));
  for (const draft of usage) {
    await apiPost<CustomFieldAssociation, CreateAssociationDto>(
      operationsClient,
      customFieldEndpoints.definitionAssociations(created.id),
      createAssociationDtoFromDraft(draft),
    );
  }
  for (const draft of usage) {
    await createRule(created.id, draft);
  }

  return created;
}

function normalizeDraftForSave(
  draft: EntityUsageDraft,
  allDrafts: EntityUsageDraft[],
): EntityUsageDraft {
  if (!draft.source) return draft;

  const sourceExists = allDrafts.some((item) => item.entityType === draft.source?.sourceEntityType);
  if (!sourceExists) return { ...draft, source: null };

  return draft;
}

function createAssociationDtoFromDraft(draft: EntityUsageDraft): CreateAssociationDto {
  return {
    entityType: draft.entityType,
    displayOrder: 0,
    jobTypeIds: jobTypeIdsForSave(draft),
    ...associationPatchFromDraft(draft),
  };
}

async function createRule(definitionId: string, draft: EntityUsageDraft) {
  if (!draft.source) return;
  await apiPost<CustomFieldInheritanceRule, CreateInheritanceRuleDto>(
    operationsClient,
    customFieldEndpoints.inheritanceRules(),
    {
      fieldDefinitionId: definitionId,
      sourceEntityType: draft.source.sourceEntityType,
      targetEntityType: draft.entityType,
      // COPY_ON_CREATE is the only behavior the API will create.
      behavior: 'COPY_ON_CREATE',
      priority: 0,
    },
  );
}
